import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, numberFormatter, formatCurrency } from '../../../helpers/utils';
import SnapshotService from '../services';
import CardEmptyResult from 'components/common/pageComponents/CardEmptyResult';
import { PiUserDuotone } from 'react-icons/pi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent
]);

const CustomerSnapshotChart = ({ limit = 10, sortBy = 'total_sales_amount', customerType, startDate, endDate }) => {
  const [currency_symbol, setCurrencySymbol] = useState('$');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getData = useCallback(() => {
    setIsLoading(true);
    const params = {
      limit: limit,
      sort_by: sortBy,
      sort_order: 'desc'
    };
    
    if (customerType) params.customer_type = customerType;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    SnapshotService.getCustomerSnapshots(params).then((response) => {
      try {
        if (response.data?.success && response.data?.data) {
          const { data: chartData, currency } = response.data.data;
          setData(chartData);
          setCurrencySymbol(currency?.currency_symbol || '$');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching customer snapshot data:', error);
        setIsLoading(false);
      }
    }).catch(error => {
      console.error('Error fetching customer snapshot data:', error);
      setIsLoading(false);
    });
  }, [limit, sortBy, customerType, startDate, endDate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const customerNames = useMemo(() => data.map(item => item.customer_name || item.customer_business_name || 'N/A'), [data]);
  const salesAmounts = useMemo(() => data.map(item => parseFloat(item.total_sales_amount) || 0), [data]);
  const outstandingAmounts = useMemo(() => data.map(item => parseFloat(item.total_outstanding_amount) || 0), [data]);

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const firstParam = params[0];
    const customerName = firstParam.name || '';
    const index = firstParam.dataIndex;
    const customer = data[index];
    
    if (!customer) return '';

    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${customerName}</div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('primary')}; margin-right: 6px;"></span>
          Total Sales: ${formatCurrency(customer.total_sales_amount, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('warning')}; margin-right: 6px;"></span>
          Outstanding: ${formatCurrency(customer.total_outstanding_amount, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          Sales Count: ${customer.total_sales_count || 0}
        </div>
      </div>
    `;
  }, [currency_symbol, data]);

  const getOptions = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: { color: '#fff' }
    },
    xAxis: {
      type: 'category',
      data: customerNames,
      axisPointer: { type: 'shadow' },
      axisLabel: {
        color: getColor('gray-400'),
        rotate: customerNames.length > 5 ? 45 : 0,
        interval: 0
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisPointer: { show: false },
      splitLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-400'),
        formatter: (value) => {
          return `${currency_symbol} ${numberFormatter(value, 0)}`
        },
        margin: 15
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        name: 'Total Sales',
        type: 'bar',
        data: salesAmounts,
        itemStyle: {
          color: getColor('primary')
        },
        barMaxWidth: 50
      },
      {
        name: 'Outstanding',
        type: 'bar',
        data: outstandingAmounts,
        itemStyle: {
          color: getColor('warning')
        },
        barMaxWidth: 50
      }
    ],
    legend: {
      data: ['Total Sales', 'Outstanding'],
      bottom: 0,
      textStyle: {
        color: getColor('gray-400')
      }
    },
    grid: { right: 10, left: 0, bottom: 30, top: 10, containLabel: true }
  }), [customerNames, currency_symbol, tooltipFormatter, salesAmounts, outstandingAmounts]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const hasData =
    data.length > 0 &&
    (salesAmounts.some(value => value > 0) || outstandingAmounts.some(value => value > 0));

  if (!hasData) {
    return (
      <CardEmptyResult
        title="No customer analytics data"
        subtitle="There is no customer analytics data available for the selected criteria."
        icon={<PiUserDuotone className="fs-2 text-secondary" />}
      />
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getOptions}
      style={{ height: '20.4375rem' }}
    />
  );
};

export default CustomerSnapshotChart;

